// client/src/components/admin/AdminProfile.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';

const AdminProfile = () => {
  const [user, setUser] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    position: ''
  });
  
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await axios.get('/api/auth/profile');
        setUser(res.data.user);
        setFormData({
          firstName: res.data.user.firstName,
          lastName: res.data.user.lastName,
          email: res.data.user.email,
          phone: res.data.user.phone || '',
          position: res.data.user.position || ''
        });
        setLoading(false);
      } catch (err) {
        setError('Failed to fetch profile');
        setLoading(false);
      }
    };
    
    fetchProfile();
  }, []);
  
  const handleProfileChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };
  
  const handlePasswordChange = (e) => {
    setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
  };
  
  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    try {
      await axios.put('/api/auth/profile', formData);
      setSuccess('Profile updated successfully');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update profile');
    }
  };
  
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');
    
    // Validate passwords match
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      return setPasswordError('Passwords do not match');
    }
    
    try {
      await axios.put('/api/auth/change-password', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      
      setPasswordSuccess('Password changed successfully');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (err) {
      setPasswordError(err.response?.data?.message || 'Failed to change password');
    }
  };
  
  if (loading) {
    return <div className="loading">Loading profile...</div>;
  }
  
  if (!user) {
    return <div className="error-message">Failed to load profile</div>;
  }
  
  return (
    <div className="admin-container">
      <h2 className="admin-page-title">Admin Profile</h2>
      
      <div className="card admin-profile-card">
        <div className="card-title">
          <span>Personal Information</span>
        </div>
        
        {error && <div className="alert-danger">{error}</div>}
        {success && <div className="alert-success">{success}</div>}
        
        <form onSubmit={handleProfileSubmit}>
          <div className="admin-form-row">
            <div className="admin-form-group">
              <label className="admin-form-label" htmlFor="firstName">First Name</label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleProfileChange}
                className="admin-form-input"
                required
              />
            </div>
            
            <div className="admin-form-group">
              <label className="admin-form-label" htmlFor="lastName">Last Name</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleProfileChange}
                className="admin-form-input"
                required
              />
            </div>
          </div>
          
          <div className="admin-form-group">
            <label className="admin-form-label" htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleProfileChange}
              className="admin-form-input"
              required
            />
          </div>
          
          <div className="admin-form-row">
            <div className="admin-form-group">
              <label className="admin-form-label" htmlFor="phone">Phone Number</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleProfileChange}
                className="admin-form-input"
              />
            </div>
            
            <div className="admin-form-group">
              <label className="admin-form-label" htmlFor="position">Position</label>
              <input
                type="text"
                id="position"
                name="position"
                value={formData.position}
                onChange={handleProfileChange}
                className="admin-form-input"
              />
            </div>
          </div>
          
          <div className="admin-form-actions">
            <button type="submit" className="btn btn-primary">
              Update Profile
            </button>
          </div>
        </form>
      </div>
      
      <div className="card admin-profile-card">
        <div className="card-title">
          <span>Change Password</span>
        </div>
        
        {passwordError && <div className="alert-danger">{passwordError}</div>}
        {passwordSuccess && <div className="alert-success">{passwordSuccess}</div>}
        
        <form onSubmit={handlePasswordSubmit}>
          <div className="admin-form-group">
            <label className="admin-form-label" htmlFor="currentPassword">Current Password</label>
            <input
              type="password"
              id="currentPassword"
              name="currentPassword"
              value={passwordData.currentPassword}
              onChange={handlePasswordChange}
              className="admin-form-input"
              required
            />
          </div>
          
          <div className="admin-form-row">
            <div className="admin-form-group">
              <label className="admin-form-label" htmlFor="newPassword">New Password</label>
              <input
                type="password"
                id="newPassword"
                name="newPassword"
                value={passwordData.newPassword}
                onChange={handlePasswordChange}
                className="admin-form-input"
                required
                minLength="8"
              />
            </div>
            
            <div className="admin-form-group">
              <label className="admin-form-label" htmlFor="confirmPassword">Confirm New Password</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={passwordData.confirmPassword}
                onChange={handlePasswordChange}
                className="admin-form-input"
                required
                minLength="8"
              />
            </div>
          </div>
          
          <div className="admin-form-actions">
            <button type="submit" className="btn btn-primary">
              Change Password
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminProfile;